import React from "react";
import { Helmet } from "react-helmet-async";
import { BUSINESS } from "@/data/business";
import { authorSchema } from "@/data/authors";
import { LOCALES, DEFAULT_LOCALE } from "@/i18n";

const SITE_URL = BUSINESS.url;
const SITE_NAME = BUSINESS.name;
const DEFAULT_OG_IMAGE = BUSINESS.logo;

/** Build a rich Organization schema with full entity data. */
function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    alternateName: "EquiSight — paarden management app",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: BUSINESS.logo,
      width: 512,
      height: 512,
    },
    image: BUSINESS.logo,
    description: BUSINESS.description,
    slogan: BUSINESS.slogan,
    email: BUSINESS.email,
    foundingDate: BUSINESS.foundingDate,
    founder: {
      "@type": "Person",
      name: BUSINESS.founder.name,
    },
    vatID: BUSINESS.vatID,
    taxID: BUSINESS.kvk,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.address.streetAddress,
      postalCode: BUSINESS.address.postalCode,
      addressLocality: BUSINESS.address.addressLocality,
      addressCountry: BUSINESS.address.addressCountry,
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: BUSINESS.email,
      contactType: "customer support",
      areaServed: BUSINESS.areaServed,
      availableLanguage: ["Dutch", "English"],
    },
    sameAs: BUSINESS.sameAs,
    areaServed: BUSINESS.areaServed,
    knowsAbout: BUSINESS.knowsAbout,
    knowsLanguage: ["nl", "en"],
  };
}

function localBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#localbusiness`,
    name: BUSINESS.legalName,
    image: BUSINESS.logo,
    url: SITE_URL,
    email: BUSINESS.email,
    priceRange: BUSINESS.priceRange,
    paymentAccepted: BUSINESS.paymentAccepted.join(", "),
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.address.streetAddress,
      postalCode: BUSINESS.address.postalCode,
      addressLocality: BUSINESS.address.addressLocality,
      addressCountry: BUSINESS.address.addressCountry,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.geo.latitude,
      longitude: BUSINESS.geo.longitude,
    },
    areaServed: BUSINESS.areaServed,
  };
}

function softwareAppJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${SITE_URL}/#softwareapp`,
    name: BUSINESS.name,
    applicationCategory: "LifestyleApplication",
    applicationSubCategory: "Paarden management",
    operatingSystem: "iOS, Android, Web",
    description: BUSINESS.description,
    softwareVersion: "1.0 BETA",
    releaseNotes: "Bèta launch op 1 september 2026.",
    inLanguage: ["nl", "en"],
    offers: [
      {
        "@type": "Offer",
        name: "Basic",
        price: "0",
        priceCurrency: "EUR",
        description: "1 paard, basis registratie, noodnummers, beperkte historie",
      },
      {
        "@type": "Offer",
        name: "Premium",
        price: "4.99",
        priceCurrency: "EUR",
        description: "Meerdere paarden, cloud backup, manege functionaliteit",
      },
      {
        "@type": "Offer",
        name: "Pro",
        price: "14.99",
        priceCurrency: "EUR",
        description: "Trainer module, fokkerij module, uitgebreide analyses",
      },
      {
        "@type": "Offer",
        name: "Professional",
        price: "24.99",
        priceCurrency: "EUR",
        description: "Hoefsmid, tandarts, fysio modules + agenda + klantbeheer",
      },
    ],
    publisher: { "@id": `${SITE_URL}/#organization` },
    creator: { "@id": `${SITE_URL}/#organization` },
    featureList: [
      "Paardendossier per paard",
      "Wedstrijdanalyse (KNHS / FEI protocol scan)",
      "Hoefsmid agenda en klantbeheer",
      "Voederplan en voeradvies",
      "Vaccinatiekalender",
      "Ontwormingsschema",
      "Noodnummers paard",
      "Fokkerij module (merrie cyclus, dracht)",
      "Trainer module",
      "Foto- en video-progressie",
    ],
  };
}

function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: BUSINESS.name,
    url: SITE_URL,
    inLanguage: "nl-NL",
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/zoeken?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * Central SEO component — handles title, description, canonical,
 * OpenGraph, Twitter cards and JSON-LD structured data.
 *
 * Usage:
 *   <Seo
 *     title="EquiSight — paarden management app"
 *     description="…"
 *     path="/functies/hoefsmid"
 *     keywords={["hoefsmid app", "paard hoefsmid planning"]}
 *     image="/assets/owner-horse.png"
 *     type="website"
 *     breadcrumbs={[{name: "Functies", path: "/functies"}, {name: "Hoefsmid", path: "/functies/hoefsmid"}]}
 *     faq={[{q: "…", a: "…"}]}
 *     article={{ headline, datePublished, dateModified, author }}
 *     howTo={{ name, totalTime, steps: [{name, text}] }}
 *   />
 */
export const Seo = ({
  title,
  description,
  path = "/",
  keywords,
  image,
  type = "website",
  breadcrumbs,
  faq,
  article,
  howTo,
  jsonLd,
  noindex = false,
}) => {
  const canonical = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  // Determine current locale from canonical path (handled by BrowserRouter basename
  // on client; here we just check the URL). The provided `path` is always the
  // un-prefixed page path (e.g. "/blog"), so canonical is in the default locale.
  // We emit hreflang for every supported locale.
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const hreflangs = LOCALES.map((l) => ({
    code: l.code === DEFAULT_LOCALE ? "nl" : l.code,
    href:
      l.code === DEFAULT_LOCALE
        ? `${SITE_URL}${cleanPath}`
        : `${SITE_URL}/${l.code}${cleanPath === "/" ? "" : cleanPath}`,
  }));
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image.startsWith("/") ? image : `/${image}`}`
    : DEFAULT_OG_IMAGE;

  const jsonLdBlocks = [organizationJsonLd(), websiteJsonLd()];

  // LocalBusiness only on home + about + contact
  if (path === "/" || path === "/over-ons" || path === "/contact") {
    jsonLdBlocks.push(localBusinessJsonLd());
  }

  // SoftwareApplication on home + functies + doelgroepen
  if (
    path === "/" ||
    path.startsWith("/functies") ||
    path.startsWith("/doelgroepen") ||
    path === "/app"
  ) {
    jsonLdBlocks.push(softwareAppJsonLd());
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        ...breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 2,
          name: b.name,
          item: `${SITE_URL}${b.path}`,
        })),
      ],
    });
  }

  if (faq && faq.length > 0) {
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  if (article) {
    const author = authorSchema(article.authorSlug, SITE_URL);
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.headline ?? title,
      description: description,
      image: ogImage,
      datePublished: article.datePublished,
      dateModified: article.dateModified ?? article.datePublished,
      author,
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: canonical,
      inLanguage: "nl-NL",
    });
  }

  // Optional custom JSON-LD payload (e.g. ItemList for hub pages).
  if (jsonLd) {
    jsonLdBlocks.push(jsonLd);
  }

  if (howTo) {
    jsonLdBlocks.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: howTo.name,
      description: howTo.description ?? description,
      totalTime: howTo.totalTime ?? "PT5M",
      step: howTo.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }

  return (
    <Helmet>
      <html lang="nl" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      <link rel="canonical" href={canonical} />
      {hreflangs.map((h) => (
        <link key={h.code} rel="alternate" hrefLang={h.code} href={h.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${cleanPath}`} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="nl_NL" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Author / publisher meta — boost AI authority signals */}
      <meta name="author" content={BUSINESS.legalName} />
      <meta name="publisher" content={BUSINESS.legalName} />

      {jsonLdBlocks.map((block, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
